import { useState } from "react";
import { Plus } from "lucide-react";

import { Badge } from "@monorepo-apps/ui/src/components/badge";
import { Button } from "@monorepo-apps/ui/src/components/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@monorepo-apps/ui/src/components/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "@monorepo-apps/ui/src/components/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@monorepo-apps/ui/src/components/table";

import { useFeatureRequests } from "@/application/use-cases/feature-requests/use-feature-requests.queries";
import { PageHeader } from "@/presentation/components/shell/page-header";
import { FeatureRequestDrawerContent } from "@/presentation/components/requests/feature-request-drawer-content";
import {
  REQUEST_STATUS_LABEL,
  REQUEST_TYPE_LABEL,
  REQUEST_TYPE_TONE,
} from "@/presentation/components/requests/feature-request-meta";
import { NewRequestDialog } from "@/presentation/components/requests/new-request-dialog";
import {
  ALL_SOURCES,
  effectiveSource,
  GROUP_PREFIX,
  IN_APP,
  matchesSource,
} from "@/presentation/components/requests/feedback-source";
import type { FeatureRequestStatus } from "@/infrastructure/supabase/database.types";

interface FeedbackPageProps {
  /** the workspace name shown above the title */
  eyebrow: string;
  /** the request open in the drawer (the route's `?id=`) */
  openId: string | null;
  onOpen: (id: string) => void;
  onClose: () => void;
}

/**
 * The feedback list (table feature_requests). One shared list, shown in both the
 * Implementation (/feedback) and B2C Implementation (/b2c/feedback) workspaces (BF-1440).
 * Two tabs: Open (the working list) and Done (finished: done, or declined with the reason in its
 * closing comment; each row's status badge tells them apart).
 */
type FeedbackTab = "open" | "done";

// Which tab a status belongs to; every other status is still open.
function tabOf(status: FeatureRequestStatus): FeedbackTab {
  return status === "done" || status === "declined" ? "done" : "open";
}

const EMPTY_TAB: Record<FeedbackTab, string> = {
  open: "No open feedback.",
  done: "Nothing finished yet.",
};

export function FeedbackPage({ eyebrow, openId, onOpen, onClose }: FeedbackPageProps) {
  const listQ = useFeatureRequests();
  const [adding, setAdding] = useState(false);
  const [tab, setTab] = useState<FeedbackTab>("open");
  const [selectedSource, setSelectedSource] = useState(ALL_SOURCES);
  const requests = listQ.data ?? [];
  const groups = [...new Set(requests.flatMap((r) => r.source_groups))].sort((a, b) =>
    a.localeCompare(b)
  );
  const source = effectiveSource(selectedSource, groups);
  const filtered = requests.filter((r) => matchesSource(r, source));
  const counts: Record<FeedbackTab, number> = { open: 0, done: 0 };
  for (const r of filtered) counts[tabOf(r.status)]++;
  const visible = filtered.filter((r) => tabOf(r.status) === tab);
  const open = requests.find((r) => r.id === openId) ?? null;

  return (
    <div className="animate-page-enter mx-auto flex w-full max-w-5xl flex-col">
      <PageHeader
        eyebrow={eyebrow}
        title="Feedback"
        summary="Log a feature, feedback, issue, or any report for the delivery team. Click a row to view the thread, attach files, and follow updates."
        actions={
          <Button size="sm" onClick={() => setAdding(true)}>
            <Plus className="size-3.5" />
            New feedback
          </Button>
        }
      />
      <div className="px-6 pt-6 pb-8 lg:px-10">
        {listQ.isError ? (
          <p className="text-muted-foreground text-xs italic">Could not load requests.</p>
        ) : listQ.isLoading ? (
          <p className="text-muted-foreground text-xs italic">Loading requests…</p>
        ) : requests.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            Nothing logged yet — click “New feedback” to add the first item.
          </p>
        ) : (
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <FeedbackTabs tab={tab} onChange={setTab} counts={counts} />
              {groups.length > 0 ? (
                <SourceFilter source={source} onChange={setSelectedSource} groups={groups} />
              ) : null}
            </div>
            {visible.length === 0 ? (
              <p className="text-muted-foreground py-6 text-sm">{EMPTY_TAB[tab]}</p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-24">Type</TableHead>
                      <TableHead>Title</TableHead>
                      <TableHead className="w-28">Status</TableHead>
                      <TableHead className="w-56">Submitter</TableHead>
                      <TableHead className="w-24">Created</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {visible.map((r) => (
                      <TableRow
                        key={r.id}
                        className="focus-visible:outline-ring cursor-pointer focus-visible:outline-2"
                        tabIndex={0}
                        onClick={() => onOpen(r.id)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            onOpen(r.id);
                          }
                        }}
                      >
                        <TableCell>
                          <Badge
                            variant="outline"
                            className="text-[10px]"
                            style={{ color: REQUEST_TYPE_TONE[r.type] }}
                          >
                            {REQUEST_TYPE_LABEL[r.type]}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-[13px] font-medium">{r.title}</TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="text-[10px]">
                            {REQUEST_STATUS_LABEL[r.status]}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground text-[11.5px]">
                          {r.submitter_email ?? "unknown"}
                          {r.source_groups.length > 0 ? (
                            <div className="text-[10.5px]" dir="auto">
                              {r.source_groups.join(" · ")}
                            </div>
                          ) : null}
                        </TableCell>
                        <TableCell className="text-muted-foreground text-[11px]">
                          {r.created_at?.slice(0, 10)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        )}
      </div>

      <Sheet open={!!open} onOpenChange={(o) => !o && onClose()}>
        <SheetContent side="right" className="w-full overflow-y-auto p-5 sm:max-w-lg">
          <SheetTitle className="text-[15px]">{open?.title ?? "Request"}</SheetTitle>
          <SheetDescription className="sr-only">
            Feedback detail — attachments and discussion.
          </SheetDescription>
          {open ? (
            <div className="mt-3 h-[calc(100%-2.5rem)]">
              <FeatureRequestDrawerContent key={open.id} request={open} onDeleted={onClose} />
            </div>
          ) : null}
        </SheetContent>
      </Sheet>

      <NewRequestDialog open={adding} onClose={() => setAdding(false)} onCreated={onOpen} />
    </div>
  );
}

interface SourceFilterProps {
  source: string;
  onChange: (source: string) => void;
  groups: string[];
}

function SourceFilter({ source, onChange, groups }: SourceFilterProps) {
  return (
    <Select value={source} onValueChange={onChange}>
      <SelectTrigger className="mb-1.5 h-8 w-64 text-xs" aria-label="Filter by source">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL_SOURCES}>All sources</SelectItem>
        <SelectItem value={IN_APP}>Logged in the app</SelectItem>
        {groups.map((g) => (
          <SelectItem key={g} value={`${GROUP_PREFIX}${g}`}>
            <span dir="auto">Telegram · {g}</span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

interface FeedbackTabsProps {
  tab: FeedbackTab;
  onChange: (tab: FeedbackTab) => void;
  counts: Record<FeedbackTab, number>;
}

function FeedbackTabs({ tab, onChange, counts }: FeedbackTabsProps) {
  const tabs: { id: FeedbackTab; label: string; count: number }[] = [
    { id: "open", label: "Open", count: counts.open },
    { id: "done", label: "Done", count: counts.done },
  ];
  return (
    <div role="tablist" aria-label="Feedback" className="border-border flex gap-1 border-b">
      {tabs.map((t) => (
        <button
          key={t.id}
          type="button"
          role="tab"
          aria-selected={tab === t.id}
          onClick={() => onChange(t.id)}
          className={`-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2 text-[13px] focus-visible:outline-2 ${
            tab === t.id
              ? "border-primary text-foreground font-semibold"
              : "text-muted-foreground hover:text-foreground border-transparent"
          }`}
        >
          {t.label}
          <span className="bg-muted rounded-full px-1.5 text-[11px] tabular-nums">{t.count}</span>
        </button>
      ))}
    </div>
  );
}
